import React, { useState } from 'react';
import {
    Typography,
    TextField,
    Box,
    IconButton,
    Popover,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { styled } from '@mui/material/styles';

const CodeBlockStyled = styled('pre')({
    backgroundColor: '#2d2d2d',
    color: '#f8f8f2',
    padding: '15px',
    borderRadius: '4px',
    fontFamily: 'Courier New, Courier, monospace',
    overflowX: 'hidden',
    wordWrap: 'break-word',
    whiteSpace: 'pre-wrap',
    margin: 0,
    position: 'relative',
    '& .tag': { color: '#f92672' },
    '& .attribute': { color: '#a6e22e' },
    '& .value': { color: '#e6db74' },
});

const CopyButton = styled(IconButton)({
    position: 'absolute',
    right: '8px',
    top: '8px',
    color: '#ffffff',
});

const BookingIframeGenerator = ({ businessUrl }) => {
    const [copied, setCopied] = useState(false);
    const [primaryColor, setPrimaryColor] = useState('#1976d2');
    const [anchorEl, setAnchorEl] = useState(null);
    const [width, setWidth] = useState('100%');
    const [height, setHeight] = useState('600px');

    const colors = ['#1976d2', '#2e7d32', '#d32f2f', '#ed6c02', '#9c27b0'];

    const generateIframeCode = () => `
    <iframe 
        src="${window.location.origin}/embed/${businessUrl}?primaryColor=${encodeURIComponent(primaryColor)}"
        width="${width}"
        height="${height}"
        frameborder="0"
        allowtransparency="true"
        style="border: none;">
    </iframe>`;

    const handleCopy = () => {
        navigator.clipboard.writeText(generateIframeCode());
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ maxWidth: '800px', margin: '0 auto', padding: '16px' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Customize Booking Widget
            </Typography>

            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Widget Dimensions</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="Width"
                        value={width}
                        onChange={(e) => setWidth(e.target.value)}
                        size="small"
                        sx={{ width: '150px' }}
                        helperText="e.g., 100%, 500px"
                    />
                    <TextField
                        label="Height"
                        value={height}
                        onChange={(e) => setHeight(e.target.value)}
                        size="small"
                        sx={{ width: '150px' }}
                        helperText="e.g., 600px"
                    />
                </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Primary Color</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: '36px',
                            height: '36px',
                            backgroundColor: primaryColor,
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            cursor: 'pointer',
                        }}
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                    />
                    <TextField
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        size="small"
                        sx={{ width: '150px' }}
                        placeholder="#000000"
                    />
                </Box>
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                    <Box sx={{ display: 'flex', gap: 1, padding: 1 }}>
                        {colors.map((color) => (
                            <Box
                                key={color}
                                sx={{
                                    width: '24px',
                                    height: '24px',
                                    backgroundColor: color,
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    cursor: 'pointer',
                                    '&:hover': { transform: 'scale(1.1)' },
                                }}
                                onClick={() => {
                                    setPrimaryColor(color);
                                    setAnchorEl(null);
                                }}
                            />
                        ))}
                    </Box>
                </Popover>
            </Box>

            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Copy and paste this code into your website:
            </Typography>
            <Box
                sx={{
                    position: 'relative',
                    backgroundColor: '#2d2d2d',
                    padding: '16px',
                    borderRadius: '4px',
                    marginBottom: 2,
                }}
            >
                <CodeBlockStyled>
                    {generateIframeCode()}
                </CodeBlockStyled>
                <CopyButton onClick={handleCopy} size="small">
                    <ContentCopyIcon />
                </CopyButton>
            </Box>
            {copied && (
                <Typography sx={{ color: 'green', mt: 1 }}>
                    Copied to clipboard!
                </Typography>
            )}
        </Box>
    );
};

export default BookingIframeGenerator;
